import { QueryClient } from '@tanstack/react-query';
import type { GetServerSideProps } from 'next';
import { storeLocale } from '@/root/constants';
import { CacheLong, CacheNone } from '@/root/strategies';
import { BlogArticleContent } from 'components/blogArticleContent/BlogArticleContent';
import { BlogArticleProvider } from 'components/blogArticleProvider/BlogArticleProvider';
import { BlogArticleNavigation } from 'components/blogNavigationsConnected/BlogNavigationsConnected';
import { BlogArticleHeader } from 'components/blogsHeaders/BlogsHeaders';
import { getAlternateUrls } from 'components/route/products/getAlternateUrls';
import { parseHandleParam } from 'components/route/products/utils/productSSRprops';
import type { GetBlogArticleQuery } from 'lib/contentful/__generated__/BlogArticleQuery';
import { useGetBlogArticleQuery } from 'lib/contentful/__generated__/BlogArticleQuery';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { getContentfulTagFilter } from 'utils/contentfulHelpers';
import type { DefaultProps } from 'utils/defaultsStaticProps';
import { defaultStaticProps } from 'utils/defaultsStaticProps';
import { sanitizeHandle } from 'utils/helpers';

export default function BlogArticlePage({ handle }: { handle: string }) {
  return (
    <BlogArticleProvider handle={handle}>
      <BlogArticleHeader />
      <BlogArticleContent />
      <BlogArticleNavigation />
    </BlogArticleProvider>
  );
}

export const getServerSideProps: GetServerSideProps<DefaultProps> = async (
  context
) => {
  const { res, preview = false } = context;

  // Set handle array from router
  const categoryParam = parseHandleParam(context.params?.category);
  const handleParam = parseHandleParam(context.params?.handle);

  const categoryArray = categoryParam.map((handle) => sanitizeHandle(handle));
  const handleArray = handleParam.map((handle) => sanitizeHandle(handle));

  // If URL is too long 404
  if (handleArray.length > 1)
    return {
      notFound: true,
    };

  // Set handle string to last value of array
  const handle = handleArray[0];
  const category = categoryArray[0];

  if (preview) {
    res.setHeader('Cache-Control', CacheNone());
  } else {
    res.setHeader('Cache-Control', CacheLong());
  }

  const locale = storeLocale(context.locale);

  const blogArticleQueryVariables = {
    handle,
    locale,
    preview,
    tagFilter: getContentfulTagFilter(locale),
  };

  const queryClient = new QueryClient();
  const [staticProps, alternateUrls] = await Promise.all([
    defaultStaticProps(context, {
      queryClient,
      queries: [
        queryClient.prefetchQuery(
          useGetBlogArticleQuery.getKey(blogArticleQueryVariables),
          useGetBlogArticleQuery.fetcher(
            contentfulQueryDataSource({ byPassCache: preview }),
            blogArticleQueryVariables
          )
        ),
      ],
    }),
    getAlternateUrls({
      handle,
      basePath: `blog/${category}`,
      contentType: 'article',
      locale,
    }),
  ]);

  // After Query Fetch, retrieve the query data held in the queryClient cache.
  const blogArticleQueryData: GetBlogArticleQuery | undefined =
    queryClient.getQueryData(
      useGetBlogArticleQuery.getKey(blogArticleQueryVariables)
    );

  if (!blogArticleQueryData?.article?.data?.length) {
    return {
      notFound: true,
    };
  }

  /**
   * 2 tests:
   *  - Check if URL category don't matches what's referenced in Article.
   *  - Check is category is not '_preview' OR no preview key passed (Expression is negated!).
   */
  if (
    category !== blogArticleQueryData?.article?.data[0]?.categoryRef?.handle &&
    !(category === '_preview' && preview)
  ) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      handle,
      ...staticProps,
      alternateUrls,
    },
  };
};
